/**
 * Pill button.
 *
 * Variants mirror the portal's `components/ui/button.tsx`, including the
 * one that matters most: `secondary` is the brand-blue (#76B7CD) fill
 * with navy text, and it is BlueSphere's actual call-to-action. MDR
 * previously had no brand blue at all, so every CTA was navy-on-navy and
 * the product read as a different family.
 *
 * Everything here is `rounded-full` because in BlueSphere almost
 * everything is — buttons, nav items, tabs, badges, even list rows.
 */

import clsx from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export interface PillButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
}

export function PillButton({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: PillButtonProps) {
  return (
    <button
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap',
        'transition-colors focus-visible:outline-hidden focus-visible:ring-2',
        'focus-visible:ring-bluesphere-secondary focus-visible:ring-offset-2',
        'focus-visible:ring-offset-bluesphere-bg',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        size === 'sm' && 'h-8 px-4 text-xs',
        size === 'md' && 'h-10 px-5 text-sm',
        size === 'lg' && 'h-11 px-8 text-sm',
        variant === 'primary' &&
          'bg-bluesphere-primary text-bluesphere-primaryFg hover:bg-bluesphere-primary/95',
        // The BlueSphere CTA.
        variant === 'secondary' &&
          'bg-bluesphere-secondary text-bluesphere-secondaryFg font-bold hover:bg-bluesphere-secondary/90',
        variant === 'outline' &&
          'border border-bluesphere-primary bg-transparent text-bluesphere-textOnLight hover:bg-black/5',
        variant === 'ghost' && 'bg-transparent hover:bg-black/10',
        variant === 'danger' &&
          'bg-bluesphere-scoreCritical text-white hover:bg-bluesphere-scoreCritical/90',
        className,
      )}
    >
      {children}
    </button>
  );
}
